import React, { useRef, useState } from 'react';
import { Box, Card, CardContent, Typography, AvatarGroup, Avatar, Tooltip, Chip } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import CalendarToday from '@mui/icons-material/CalendarToday';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { WorkItem, Column } from 'api/todo/types';

const CANTRACK_CASE_URL = '/cantrack/cases';

interface TaskCardProps {
  task: WorkItem;
  index: number;
  columns: Column[];
  onClick: () => void;
}

export const TaskCard: React.FC<TaskCardProps> = ({ task, index, columns, onClick }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOver, setDragOver] = useState<'top' | 'bottom' | null>(null);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'task',
      taskId: task.work_item_id,
      columnId: task.column_id,
      index,
    }));
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const rect = ref.current?.getBoundingClientRect();
    if (rect) {
      const midY = rect.top + rect.height / 2;
      setDragOver(e.clientY < midY ? 'top' : 'bottom');
    }
  };

  const handleDragLeave = () => {
    setDragOver(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(null);
  };

  const truncateText = (text: string | null, maxLength: number): string => {
    if (!text || typeof text !== 'string') return '';
    if (maxLength <= 0) return text;
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const handleClick = () => {
    if (!isDragging) {
      onClick();
    }
  };

  return (
    <>
      {dragOver === 'top' && (
        <Box
          sx={{
            height: 2,
            backgroundColor: 'primary.main',
            mb: 0.5,
            borderRadius: 1,
          }}
        />
      )}
      <Card
        ref={ref}
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        data-task-id={task.work_item_id}
        data-column-id={task.column_id}
        data-index={index}
        sx={{
          mb: 1,
          cursor: isDragging ? 'grabbing' : 'grab',
          opacity: isDragging ? 0.5 : 1,
          boxShadow: isDragging ? 3 : dragOver ? 2 : 1,
          transform: isDragging ? 'rotate(2deg)' : 'none',
          transition: isDragging ? 'none' : 'box-shadow 0.2s ease',
          '&:hover': {
            boxShadow: isDragging ? 3 : 2,
          },
        }}
      >
        <CardContent sx={{ p: { xs: 1, sm: 1.5 }, '&:last-child': { pb: { xs: 1, sm: 1.5 } } }}>
          <Typography variant="subtitle2" fontWeight="medium" gutterBottom sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
            {task.title}
          </Typography>
          {task.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
              {truncateText(task.description, 100)}
            </Typography>
          )}
          {task.case_title && task.case_id && (
            <Chip
              icon={<LinkIcon sx={{ fontSize: '14px !important' }} />}
              label={task.case_title}
              size="small"
              variant="outlined"
              clickable
              onClick={(e) => {
                e.stopPropagation();
                window.location.href = `${CANTRACK_CASE_URL}/${task.case_id}`;
              }}
              sx={{ mb: 1, maxWidth: '100%', minHeight: { xs: 32, sm: 24 }, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
            />
          )}
          {task.date_due && (() => {
            const sortedCols = [...columns].sort((a, b) => a.sort_order - b.sort_order);
            const doneColumnId = sortedCols.length > 0 ? sortedCols[sortedCols.length - 1].column_id : null;
            const isDone = doneColumnId !== null && task.column_id === doneColumnId;
            const isOverdue = new Date(task.date_due) < new Date() && !isDone;
            return (
              <Chip
                icon={<CalendarToday sx={{ fontSize: '14px !important' }} />}
                label={new Date(task.date_due).toLocaleDateString()}
                size="small"
                color={isOverdue ? 'error' : 'default'}
                sx={{ mb: 1 }}
              />
            );
          })()}
          {task.checklist && task.checklist.length > 0 && (
            <Chip
              icon={<CheckBoxIcon sx={{ fontSize: '14px !important' }} />}
              label={`${task.checklist.filter((i) => i.completed).length}/${task.checklist.length} sub-tasks`}
              size="small"
              variant="outlined"
              sx={{ mb: 1 }}
            />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            {task.assignees && task.assignees.length > 0 ? (
              <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}>
                {task.assignees.map((assignee) => (
                  <Tooltip key={assignee.user_id} title={assignee.name}>
                    <Avatar>{assignee.name.charAt(0).toUpperCase()}</Avatar>
                  </Tooltip>
                ))}
              </AvatarGroup>
            ) : <Box />}
            {task.primary_work_item_id != null && (
              <Tooltip title="Linked from another board">
                <LinkIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </Tooltip>
            )}
          </Box>
        </CardContent>
      </Card>
      {dragOver === 'bottom' && (
        <Box
          sx={{
            height: 2,
            backgroundColor: 'primary.main',
            mt: 0.5,
            borderRadius: 1,
          }}
        />
      )}
    </>
  );
};
